import React, { useState } from 'react';
import { TableRow, TableCell, Chip, Typography, Tooltip } from '@mui/material';
import { format } from 'date-fns';

import {
  CollectWiseRetellPhoneCall,
  isPhoneCall,
  isWebCall,
} from '../../api/hooks/calls/getCallList';
import RecordingDownloadButtonComponent from './RecordingDownloadButtonComponent';
import RetellCallDetailsComponent from './RetellCallDetailsComponent';

interface RetellCallComponentProps {
  call: CollectWiseRetellPhoneCall;
}

interface CallAnalysis {
  user_sentiment?: string;
}

function resolveCallAnalysis(
  call: CollectWiseRetellPhoneCall
): CallAnalysis | undefined {
  const record = call as unknown as Record<string, unknown>;
  if (
    record.scrubbed_call_analysis &&
    typeof record.scrubbed_call_analysis === 'object'
  ) {
    return record.scrubbed_call_analysis as CallAnalysis;
  }
  return call.call_analysis as CallAnalysis | undefined;
}

const RetellCallComponent: React.FC<RetellCallComponentProps> = ({ call }) => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const callAnalysis = resolveCallAnalysis(call);

  const handleOpenDetails = () => {
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
  };

  const formatDuration = (seconds: number): string => {
    if (!seconds) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatTimestamp = (timestamp: number): string => {
    return format(new Date(timestamp), 'MMM dd, yyyy HH:mm');
  };

  const getSentimentColor = (sentiment?: string) => {
    if (!sentiment) return 'default';

    switch (sentiment.toLowerCase()) {
      case 'positive':
        return 'success';
      case 'negative':
        return 'info';
      case 'neutral':
        return 'info';
      default:
        return 'default';
    }
  };

  const getCallDuration = (): number => {
    if (call.start_timestamp && call.end_timestamp) {
      return Math.floor((call.end_timestamp - call.start_timestamp) / 1000);
    }
    return 0;
  };

  return (
    <>
      <TableRow
        hover
        onClick={() => {
          handleOpenDetails();
        }}
        sx={{
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <TableCell>
          {call.start_timestamp ? formatTimestamp(call.start_timestamp) : 'N/A'}
        </TableCell>
        <TableCell>
          {call.start_timestamp && call.end_timestamp
            ? formatDuration(getCallDuration())
            : 'N/A'}
        </TableCell>
        <TableCell>
          {call.debtorFirstName} {call.debtorLastName}
        </TableCell>
        <TableCell>{call.clientId ?? 'N/A'}</TableCell>
        <TableCell>{call.fileNumber ?? 'N/A'}</TableCell>
        <TableCell>{isWebCall(call) ? 'Web Call' : 'Phone Call'}</TableCell>
        <TableCell>
          <Tooltip title={call.call_id}>
            <Typography
              variant="body2"
              sx={{
                maxWidth: 150,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {call.call_id}
            </Typography>
          </Tooltip>
        </TableCell>
        <TableCell>{isPhoneCall(call) ? call.to_number : 'N/A'}</TableCell>
        <TableCell>{isPhoneCall(call) ? call.from_number : 'N/A'}</TableCell>
        <TableCell>
          <RecordingDownloadButtonComponent call={call} />
        </TableCell>
        <TableCell>{call.disconnection_reason || 'N/A'}</TableCell>
        <TableCell>
          <Chip
            label={
              callAnalysis?.user_sentiment === 'Negative'
                ? 'Neutral'
                : callAnalysis?.user_sentiment || 'Unknown'
            }
            color={getSentimentColor(callAnalysis?.user_sentiment)}
            size="small"
          />
        </TableCell>
      </TableRow>

      {/* Call Details Side Drawer */}
      <RetellCallDetailsComponent
        call={call}
        open={detailsOpen}
        onClose={handleCloseDetails}
      />
    </>
  );
};

export default RetellCallComponent;
